# Card tokenization

The integrator's server should never see raw card data. N4 provides `@n4/checkout-js`, a ~1.5 KB
browser SDK that tokenizes a card directly against the provider's tokenization endpoint and returns
a one-time-use token your backend can pass to N4.

This keeps you on **PCI DSS SAQ-A** scope (the lightest tier) — raw card data flows browser →
provider, never browser → your server → N4.

## Browser usage

```html
<script src="https://api.n4-payments.com.br/v1/js"></script>
<script>
  N4Checkout.setPublicKey('pk_test_...');
  N4Checkout.setMode('test');

  // From your card form:
  const token = await N4Checkout.encrypt({
    number: '4235647728025682',
    holderName: 'JOAO SILVA',
    expMonth: 11,
    expYear: 2030,
    cvv: '123',
  });

  // POST that token to YOUR backend
  await fetch('/checkout/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, orderId: 'order-42' }),
  });
</script>
```

## Module usage (bundler)

```javascript
import { N4Checkout } from '@n4/checkout-js';

N4Checkout.setPublicKey(process.env.NEXT_PUBLIC_N4_PK);
N4Checkout.setMode('test');

const token = await N4Checkout.encrypt({ /* ... */ });
```

## Your backend → N4

```bash
curl -u "$PUB:$SEC" \
  -H 'Content-Type: application/json' \
  -X POST https://api.n4-payments.com.br/api/v1/transactions \
  -d '{
    "amount": 9990,
    "paymentMethod": "credit_card",
    "installments": 3,
    "customer": {"name": "Joao Silva", "email": "joao@example.com"},
    "card": {
      "token": "abc123...",
      "holderName": "JOAO SILVA"
    },
    "deviceId": "armOr0exhCFi200441132380ws0Bb1FHnH",
    "externalRef": "order-42"
  }'
```

## Device fingerprint — +5-15% approval

If you already load the MP SDK in the browser (you do, for tokenization), grab the
auto-populated `window.MP_DEVICE_SESSION_ID` and pass it as `deviceId` in the JSON body.
N4 forwards it to the provider as `additional_info.session_id`. MP's risk engine uses it
as a strong antifraud signal — published lift is 5-15% on approval rate.

```javascript
// Right before your POST to N4:
const body = {
  amount: 9990,
  paymentMethod: 'credit_card',
  card: { token, holderName: 'JOAO SILVA' },
  deviceId: window.MP_DEVICE_SESSION_ID, // ← add this line
  // ...
};
```

Non-blocking: if `MP_DEVICE_SESSION_ID` is `undefined` (script blocker, SDK not yet ready),
omit the field. N4 accepts both. There's no server-side equivalent that matches its quality —
shipping without it costs the approval boost.

The SDK throws on validation failures with a `.code` field:

| Code | Meaning |
|---|---|
| `invalid_card_number` | Number didn't pass digits-only / length check |
| `invalid_holder_name` | Empty or whitespace-only |
| `invalid_expiration_month` | Outside 1-12 |
| `invalid_expiration_year` | In the past or > 30y future |
| `invalid_cvv` | Not 3-4 digits |
| `mp_http_400` (and similar) | Provider tokenization endpoint rejected the card |
| `network_error` | Fetch failed before getting a response |

## Falling back to raw card (legacy)

Integrators that already tokenize server-side (e.g., with their own PCI infrastructure) can pass
raw card fields directly instead of a `token`:

```json
{
  "card": {
    "number": "4235647728025682",
    "holderName": "JOAO SILVA",
    "expirationMonth": 11,
    "expirationYear": 2030,
    "cvv": "123"
  }
}
```

N4 will server-side tokenize this against the provider, then process the payment. This path puts
you on PCI SAQ-D scope (you're handling raw card data). Use the SDK path unless you have a strong
reason not to.
